import React, { useState, useRef, useCallback, lazy, Suspense } from 'react';
import { C } from '../components/design-system/primitives';
import {
  Upload, Youtube, Play, ChevronDown, ChevronUp,
  Video, Loader2, FileText, Activity, Shield, Crosshair, Film, Camera,
} from 'lucide-react';
import { analyzeVideo } from '../utils/videoAnalyticsApi';
import useMobileDetect from '../hooks/useMobileDetect';

// Lazy-load heavy components — only when user interacts
const VideoAnalyticsTable = lazy(() => import('../components/VideoAnalyticsTable'));

// ---------------------------------------------------------------------------
// Analysis modes — unified (replaces 3 redundant tab configs)
// ---------------------------------------------------------------------------
const MODES = [
  { id: 'general',      label: 'General',       icon: Play,      desc: 'Mechanics, footwork, movement patterns' },
  { id: 'biomechanics', label: 'Biomechanics',  icon: Activity,  desc: 'Body mechanics, injury prevention' },
  { id: 'shooting',     label: 'Shooting Form', icon: Crosshair, desc: 'Release, arc, footwork, consistency' },
  { id: 'defense',      label: 'Team Defense',  icon: Shield,    desc: 'Rotations, help-side, communication' },
  { id: 'game-film',    label: 'Game Film',     icon: Film,      desc: 'Play-by-play tactical breakdown' },
  { id: 'deep-report',  label: 'Deep Report',   icon: FileText,  desc: 'PDF export, performance table' },
];

// Map mode IDs to the instruction strings expected by the backend
const MODE_INSTRUCTIONS = {
  'general': 'Analyze shooting mechanics, footwork, and movement patterns',
  'biomechanics': 'Analyze movement efficiency, body mechanics, and injury prevention. Focus on joint angles, balance, and power output.',
  'shooting': 'Evaluate shot release, arc angle, footwork, and consistency',
  'defense': 'Evaluate defensive rotations, help-side positioning, and communication breakdowns',
  'game-film': 'Comprehensive play-by-play tactical breakdown with coaching timestamps',
  'deep-report': 'Comprehensive analysis with performance table and film session guide',
};

// ---------------------------------------------------------------------------
// Drop zone component (desktop)
// ---------------------------------------------------------------------------
function DropZone({ file, onFile, youtubeUrl, onYoutubeUrl, inputMode, onInputMode }) {
  const ref = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f && (f.type.startsWith('video/') || f.name.match(/\.(mp4|mov|avi|mkv|webm)$/i))) {
      onFile(f);
      onInputMode('file');
    }
  }, [onFile, onInputMode]);

  const handleFileSelect = useCallback((e) => {
    const f = e.target.files?.[0];
    if (f && (f.type.startsWith('video/') || f.name.match(/\.(mp4|mov|avi|mkv|webm)$/i))) {
      onFile(f); onInputMode('file');
    }
  }, [onFile, onInputMode]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12 }}>
      {/* Toggle: File / YouTube */}
      <div style={{ display: 'flex', gap: 4 }}>
        {[
          { id: 'file', label: 'File Upload', icon: Upload },
          { id: 'youtube', label: 'YouTube', icon: Youtube },
        ].map(opt => (
          <button
            key={opt.id}
            onClick={() => onInputMode(opt.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 12px', borderRadius: 100, cursor: 'pointer',
              border: `1px solid ${inputMode === opt.id ? C.gold + '44' : C.border}`,
              background: inputMode === opt.id ? C.gold + '12' : 'transparent',
              color: inputMode === opt.id ? C.gold : C.t3,
              fontSize: 11, fontWeight: 500, transition: 'all .25s',
            }}
          >
            <opt.icon size={11} />
            {opt.label}
          </button>
        ))}
      </div>

      {inputMode === 'file' ? (
        /* ---- File drop zone ---- */
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => ref.current?.click()}
          style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 10,
            borderRadius: 14, cursor: 'pointer', transition: 'all .3s',
            border: `1.5px dashed ${dragOver ? C.gold : file ? C.gold + '44' : C.border}`,
            background: dragOver ? C.gold + '08' : file ? C.gold + '06' : C.s1,
          }}
        >
          <input
            ref={ref}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {file ? (
            <>
              <Video size={20} style={{ color: C.gold }} />
              <span style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>{file.name}</span>
              <span style={{ fontSize: 10, color: C.t3 }}>
                {(file.size / (1024 * 1024)).toFixed(1)} MB
              </span>
            </>
          ) : (
            <>
              <Upload size={20} style={{ color: C.t3 }} />
              <span style={{ fontSize: 12, color: C.t2 }}>Drop video here</span>
              <span style={{ fontSize: 10, color: C.t3, letterSpacing: 0.5 }}>
                MP4 · MOV · AVI · WEBM
              </span>
            </>
          )}
        </div>
      ) : (
        /* ---- YouTube URL ---- */
        <div
          style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            justifyContent: 'center', gap: 12, padding: '0 4px',
          }}
        >
          <div style={{ position: 'relative' }}>
            <Youtube
              size={14}
              style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: youtubeUrl ? C.gold : C.t3,
              }}
            />
            <input
              type="text"
              placeholder="Paste YouTube URL..."
              value={youtubeUrl}
              onChange={(e) => onYoutubeUrl(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px 10px 34px',
                borderRadius: 10, fontSize: 12, color: C.text,
                background: C.s1, border: `1px solid ${youtubeUrl ? C.gold + '44' : C.border}`,
                outline: 'none', transition: 'border .25s',
                fontFamily: "'Inter', system-ui, sans-serif",
              }}
            />
          </div>
          <span style={{ fontSize: 10, color: C.t3, paddingLeft: 4 }}>
            Supports public and unlisted YouTube videos
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mode selector (desktop — vertical radio list)
// ---------------------------------------------------------------------------
function ModeSelector({ activeMode, onSelect, customFocus, onCustomFocus }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        fontSize: 9, fontWeight: 600, color: C.t3,
        letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10,
      }}>
        Analysis Mode
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        {MODES.map(mode => {
          const active = activeMode === mode.id;
          const Icon = mode.icon;
          return (
            <button
              key={mode.id}
              onClick={() => onSelect(mode.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', borderRadius: 10, cursor: 'pointer',
                border: `1px solid ${active ? C.gold + '33' : 'transparent'}`,
                background: active ? C.gold + '0A' : 'transparent',
                transition: 'all .2s', textAlign: 'left', width: '100%',
              }}
            >
              {/* Radio dot */}
              <div style={{
                width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                border: `1.5px solid ${active ? C.gold : C.t3}`,
                background: active ? C.gold : 'transparent',
                boxShadow: active ? `0 0 6px ${C.gold}66` : 'none',
                transition: 'all .2s',
              }} />
              <Icon size={12} style={{ color: active ? C.gold : C.t3, flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: 12, fontWeight: 500, color: active ? C.gold : C.text,
                  letterSpacing: 0.2,
                }}>
                  {mode.label}
                </div>
                <div style={{
                  fontSize: 10, color: active ? C.t2 : C.t3,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {mode.desc}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom focus */}
      <input
        type="text"
        placeholder="Custom focus (optional)..."
        value={customFocus}
        onChange={(e) => onCustomFocus(e.target.value)}
        style={{
          width: '100%', padding: '8px 12px', marginTop: 8,
          borderRadius: 10, fontSize: 11, color: C.text,
          background: C.s1, border: `1px solid ${C.border}`,
          outline: 'none', fontFamily: "'Inter', system-ui, sans-serif",
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile upload section — replaces drag-and-drop with tap-friendly buttons
// ---------------------------------------------------------------------------
function MobileUpload({ file, onFile, youtubeUrl, onYoutubeUrl, showYouTube, onToggleYouTube }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFileSelect = useCallback((e) => {
    const f = e.target.files?.[0];
    if (f && (f.type.startsWith('video/') || f.name.match(/\.(mp4|mov|avi|mkv|webm)$/i))) {
      onFile(f);
    }
  }, [onFile]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Hidden file inputs */}
      <input ref={fileRef} type="file" accept="video/*" onChange={handleFileSelect} style={{ display: 'none' }} />
      <input ref={cameraRef} type="file" accept="video/*" capture="environment" onChange={handleFileSelect} style={{ display: 'none' }} />

      {/* Primary: Upload from Camera Roll */}
      <button
        onClick={() => fileRef.current?.click()}
        style={{
          width: '100%', padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
          border: `1.5px solid ${file ? C.gold + '44' : C.gold + '33'}`,
          background: file ? C.gold + '12' : C.gold,
          color: file ? C.gold : '#000',
          fontSize: 14, fontWeight: 600, letterSpacing: 0.3,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'all .25s',
        }}
      >
        <Upload size={16} />
        {file ? 'Change Video' : 'Upload from Camera Roll'}
      </button>

      {/* Secondary: Record Video */}
      <button
        onClick={() => cameraRef.current?.click()}
        style={{
          width: '100%', padding: '11px 16px', borderRadius: 12, cursor: 'pointer',
          border: `1px solid ${C.border}`,
          background: C.s1, color: C.t2,
          fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'all .25s',
        }}
      >
        <Camera size={15} />
        Record Video
      </button>

      {/* File info */}
      {file && (
        <div style={{
          padding: '8px 12px', borderRadius: 10,
          background: C.gold + '08', border: `1px solid ${C.gold}22`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Video size={14} style={{ color: C.gold, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: C.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {file.name}
            </div>
            <div style={{ fontSize: 10, color: C.t3 }}>
              {(file.size / (1024 * 1024)).toFixed(1)} MB
            </div>
          </div>
        </div>
      )}

      {/* YouTube toggle */}
      <button
        onClick={onToggleYouTube}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '8px', cursor: 'pointer',
          border: 'none', background: 'transparent',
          color: showYouTube ? C.gold : C.t3,
          fontSize: 11, fontWeight: 500,
        }}
      >
        <Youtube size={12} />
        {showYouTube ? 'Hide YouTube URL' : 'Or paste a YouTube URL'}
      </button>

      {/* YouTube input (collapsible) */}
      {showYouTube && (
        <div style={{ position: 'relative' }}>
          <Youtube
            size={14}
            style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: youtubeUrl ? C.gold : C.t3,
            }}
          />
          <input
            type="text"
            placeholder="Paste YouTube URL..."
            value={youtubeUrl}
            onChange={(e) => onYoutubeUrl(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px 10px 34px',
              borderRadius: 10, fontSize: 13, color: C.text,
              background: C.s1, border: `1px solid ${youtubeUrl ? C.gold + '44' : C.border}`,
              outline: 'none', fontFamily: "'Inter', system-ui, sans-serif",
            }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile mode pills — horizontal scrollable
// ---------------------------------------------------------------------------
function MobileModePills({ activeMode, onSelect }) {
  return (
    <div style={{
      display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch',
      padding: '2px 0', scrollbarWidth: 'none',
    }}>
      {MODES.map(mode => {
        const active = activeMode === mode.id;
        const Icon = mode.icon;
        return (
          <button
            key={mode.id}
            onClick={() => onSelect(mode.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
              padding: '7px 12px', borderRadius: 100, cursor: 'pointer',
              border: `1px solid ${active ? C.gold + '44' : C.border}`,
              background: active ? C.gold + '15' : C.s1,
              color: active ? C.gold : C.t2,
              fontSize: 12, fontWeight: 500, transition: 'all .2s',
              whiteSpace: 'nowrap',
            }}
          >
            <Icon size={12} />
            {mode.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function VideoAnalyticsPage() {
  const { isMobile } = useMobileDetect();

  // Upload state
  const [file, setFile] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [inputMode, setInputMode] = useState('file');
  const [showMobileYouTube, setShowMobileYouTube] = useState(false);

  // Analysis state
  const [activeMode, setActiveMode] = useState('general');
  const [customFocus, setCustomFocus] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  // History drawer
  const [showHistory, setShowHistory] = useState(false);

  // Escape key to dismiss result overlay
  React.useEffect(() => {
    if (!analysisResult) return;
    const handler = (e) => { if (e.key === 'Escape') setAnalysisResult(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [analysisResult]);

  const canAnalyze = !isAnalyzing && (file || youtubeUrl.trim());

  const handleAnalyze = useCallback(async () => {
    if (isAnalyzing || (!file && !youtubeUrl.trim())) return;
    setIsAnalyzing(true);
    setError(null);
    setAnalysisResult(null);

    const instructions = customFocus.trim()
      ? `${MODE_INSTRUCTIONS[activeMode]}. Additional focus: ${customFocus}`
      : MODE_INSTRUCTIONS[activeMode];

    try {
      const result = await analyzeVideo({
        videoFile: (isMobile ? true : inputMode === 'file') ? file : null,
        youtubeUrl: (isMobile ? showMobileYouTube : inputMode === 'youtube') ? youtubeUrl : '',
        instructions,
      });
      setAnalysisResult(result);
    } catch (err) {
      console.error('[FilmRoom] Analysis error:', err);
      setError(err.message || 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  }, [isAnalyzing, file, youtubeUrl, inputMode, activeMode, customFocus, isMobile, showMobileYouTube]);

  // =========================================================================
  // MOBILE LAYOUT
  // =========================================================================
  if (isMobile) {
    return (
      <div
        style={{
          background: C.bg,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          color: C.text,
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          overflow: 'hidden',
        }}
      >
        {/* Compact header */}
        <div style={{
          padding: '12px 16px 10px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.gold }} />
              <span style={{ fontSize: 8, fontWeight: 600, color: C.gold, letterSpacing: 2, textTransform: 'uppercase' }}>
                Film Room
              </span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 200, letterSpacing: -0.5, color: C.text }}>
              Video Analytics
            </div>
          </div>
          <button
            onClick={() => setShowHistory(p => !p)}
            style={{
              padding: '5px 10px', borderRadius: 100, cursor: 'pointer',
              border: `1px solid ${showHistory ? C.gold + '44' : C.border}`,
              background: showHistory ? C.gold + '12' : 'transparent',
              color: showHistory ? C.gold : C.t3,
              fontSize: 10, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <Video size={10} />
            History
          </button>
        </div>

        {!showHistory ? (
          <>
            {/* Scrollable content area */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Upload section */}
              <MobileUpload
                file={file}
                onFile={setFile}
                youtubeUrl={youtubeUrl}
                onYoutubeUrl={setYoutubeUrl}
                showYouTube={showMobileYouTube}
                onToggleYouTube={() => setShowMobileYouTube(p => !p)}
              />

              {/* Mode pills (horizontal scroll) */}
              <div>
                <div style={{ fontSize: 9, fontWeight: 600, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
                  Analysis Mode
                </div>
                <MobileModePills activeMode={activeMode} onSelect={setActiveMode} />
              </div>

              {/* Custom focus */}
              <input
                type="text"
                placeholder="Custom focus (optional)..."
                value={customFocus}
                onChange={(e) => setCustomFocus(e.target.value)}
                style={{
                  width: '100%', padding: '10px 14px',
                  borderRadius: 12, fontSize: 13, color: C.text,
                  background: C.s1, border: `1px solid ${C.border}`,
                  outline: 'none', fontFamily: "'Inter', system-ui, sans-serif",
                }}
              />

              {/* Error display */}
              {error && (
                <div style={{
                  fontSize: 12, color: C.dev, padding: '8px 12px', borderRadius: 10,
                  background: C.dev + '10', border: `1px solid ${C.dev}22`,
                }}>
                  {error}
                </div>
              )}
            </div>

            {/* Sticky analyze button at bottom */}
            <div style={{
              flexShrink: 0, padding: '12px 16px',
              borderTop: `1px solid ${C.border}`,
              background: C.bg,
              paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
            }}>
              <button
                onClick={handleAnalyze}
                disabled={!canAnalyze}
                style={{
                  width: '100%', padding: '13px 0', borderRadius: 100,
                  border: 'none', cursor: canAnalyze ? 'pointer' : 'not-allowed',
                  background: canAnalyze ? C.gold : C.s3,
                  color: canAnalyze ? '#000' : C.t3,
                  fontSize: 15, fontWeight: 600, letterSpacing: 0.3,
                  transition: 'all .25s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Play size={15} />
                    Analyze
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, padding: '16px', overflow: 'auto' }}>
            <Suspense fallback={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10 }}>
                <Loader2 size={16} style={{ color: C.gold, animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: 11, color: C.t3, letterSpacing: 1, textTransform: 'uppercase' }}>Loading history</span>
              </div>
            }>
              <VideoAnalyticsTable />
            </Suspense>
          </div>
        )}

        {/* Analysis result overlay — mobile-padded */}
        {analysisResult && (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) setAnalysisResult(null); }}
            style={{
              position: 'fixed', inset: 0, zIndex: 100,
              background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(8px)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{
              flex: 1, overflow: 'auto',
              background: C.s1, margin: '8px',
              borderRadius: 16, border: `1px solid ${C.border}`,
              padding: '20px 16px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 600, color: C.gold, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
                    Analysis Complete
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 300, color: C.text }}>
                    {MODES.find(m => m.id === activeMode)?.label || 'Analysis'}
                  </div>
                </div>
                <button
                  onClick={() => setAnalysisResult(null)}
                  style={{
                    padding: '6px 12px', borderRadius: 100, cursor: 'pointer',
                    border: `1px solid ${C.border}`, background: 'transparent',
                    color: C.t2, fontSize: 11, fontWeight: 500,
                  }}
                >
                  Close
                </button>
              </div>
              <div style={{
                fontSize: 13, lineHeight: 1.7, color: C.t2,
                whiteSpace: 'pre-wrap', fontFamily: "'Inter', system-ui, sans-serif",
              }}>
                {typeof analysisResult === 'string'
                  ? analysisResult
                  : analysisResult?.analysis || analysisResult?.result || JSON.stringify(analysisResult, null, 2)
                }
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  // =========================================================================
  // DESKTOP LAYOUT (unchanged)
  // =========================================================================
  return (
    <div
      style={{
        background: C.bg,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        color: C.text,
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        overflow: 'hidden',
      }}
    >
      {/* ================================================================== */}
      {/* Header — compact, GM Dashboard style                               */}
      {/* ================================================================== */}
      <div style={{
        padding: '18px 24px 14px',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.gold }} />
            <span style={{
              fontSize: 9, fontWeight: 600, color: C.gold,
              letterSpacing: 3, textTransform: 'uppercase',
            }}>
              Film Room
            </span>
          </div>
          <div style={{
            fontSize: 24, fontWeight: 200, letterSpacing: -0.8, lineHeight: 1.1,
            color: C.text,
          }}>
            Video Analytics
          </div>
        </div>

        {/* History toggle */}
        <button
          onClick={() => setShowHistory(p => !p)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', borderRadius: 100, cursor: 'pointer',
            border: `1px solid ${showHistory ? C.gold + '44' : C.border}`,
            background: showHistory ? C.gold + '12' : 'transparent',
            color: showHistory ? C.gold : C.t3,
            fontSize: 11, fontWeight: 500, transition: 'all .25s',
          }}
        >
          <Video size={12} />
          History
          {showHistory ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
      </div>

      {/* ================================================================== */}
      {/* Main content — two-column, viewport-fitted                         */}
      {/* ================================================================== */}
      {!showHistory ? (
        <div style={{
          flex: 1, display: 'flex', gap: 16,
          padding: '20px 24px', minHeight: 0,
        }}>
          {/* ---- LEFT: Upload zone (60%) ---- */}
          <div style={{
            flex: 3, display: 'flex', flexDirection: 'column',
            background: C.s1, borderRadius: 14,
            border: `1px solid ${C.border}`, padding: 20,
          }}>
            <DropZone
              file={file}
              onFile={setFile}
              youtubeUrl={youtubeUrl}
              onYoutubeUrl={setYoutubeUrl}
              inputMode={inputMode}
              onInputMode={setInputMode}
            />
          </div>

          {/* ---- RIGHT: Mode selector + action (40%) ---- */}
          <div style={{
            flex: 2, display: 'flex', flexDirection: 'column',
            background: C.s1, borderRadius: 14,
            border: `1px solid ${C.border}`, padding: 20,
          }}>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ModeSelector
                activeMode={activeMode}
                onSelect={setActiveMode}
                customFocus={customFocus}
                onCustomFocus={setCustomFocus}
              />
            </div>

            {/* Error display */}
            {error && (
              <div style={{
                fontSize: 11, color: C.dev, marginTop: 8,
                padding: '6px 10px', borderRadius: 8,
                background: C.dev + '10', border: `1px solid ${C.dev}22`,
              }}>
                {error}
              </div>
            )}

            {/* Analyze button */}
            <button
              onClick={handleAnalyze}
              disabled={!canAnalyze}
              style={{
                marginTop: 12, padding: '10px 0', borderRadius: 100,
                border: 'none', cursor: canAnalyze ? 'pointer' : 'not-allowed',
                background: canAnalyze ? C.gold : C.s3,
                color: canAnalyze ? '#000' : C.t3,
                fontSize: 13, fontWeight: 600, letterSpacing: 0.5,
                transition: 'all .25s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  Analyzing...
                </>
              ) : (
                <>
                  <Play size={13} />
                  Analyze
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        /* ================================================================ */
        /* History view — full width, scrollable                            */
        /* ================================================================ */
        <div style={{
          flex: 1, padding: '20px 24px', overflow: 'auto',
        }}>
          <Suspense fallback={
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: 200, gap: 10,
            }}>
              <Loader2 size={16} style={{ color: C.gold, animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 11, color: C.t3, letterSpacing: 1, textTransform: 'uppercase' }}>
                Loading history
              </span>
            </div>
          }>
            <VideoAnalyticsTable />
          </Suspense>
        </div>
      )}

      {/* ================================================================== */}
      {/* Analysis result overlay (when analysis completes)                   */}
      {/* ================================================================== */}
      {analysisResult && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setAnalysisResult(null); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, cursor: 'pointer',
          }}
        >
          <div style={{
            background: C.s1, borderRadius: 16,
            border: `1px solid ${C.border}`,
            maxWidth: 800, width: '100%', maxHeight: '85vh',
            overflow: 'auto', padding: 28,
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 20,
            }}>
              <div>
                <div style={{
                  fontSize: 9, fontWeight: 600, color: C.gold,
                  letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4,
                }}>
                  Analysis Complete
                </div>
                <div style={{ fontSize: 18, fontWeight: 300, color: C.text }}>
                  {MODES.find(m => m.id === activeMode)?.label || 'Analysis'}
                </div>
              </div>
              <button
                onClick={() => setAnalysisResult(null)}
                style={{
                  padding: '6px 14px', borderRadius: 100, cursor: 'pointer',
                  border: `1px solid ${C.border}`, background: 'transparent',
                  color: C.t2, fontSize: 11, fontWeight: 500,
                }}
              >
                Close
              </button>
            </div>
            <div style={{
              fontSize: 13, lineHeight: 1.7, color: C.t2,
              whiteSpace: 'pre-wrap', fontFamily: "'Inter', system-ui, sans-serif",
            }}>
              {typeof analysisResult === 'string'
                ? analysisResult
                : analysisResult?.analysis || analysisResult?.result || JSON.stringify(analysisResult, null, 2)
              }
            </div>
          </div>
        </div>
      )}

      {/* Keyframes */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
